/**
 * Star-rating arithmetic for the SERP block in MapDataPOIDetailDialog.
 *
 * Extracted from the template because Vue's `renderList` builds `new
 * Array(source)` when a `v-for` source is a number, and that throws
 * `RangeError: Invalid array length` for anything that isn't a non-negative
 * integer. Three separate loops in that dialog were exposed to it:
 *
 *  - a per-review `v-for="n in rev.rating"` with no rounding at all, so any
 *    fractional rating (Google routinely returns 4.5) crashed the render;
 *  - `v-for="n in 5 - Math.round(serpRating)"`, which goes negative the
 *    moment a payload reports a rating above 5;
 *  - `serpRating.toFixed(1)`, which is a TypeError rather than a RangeError
 *    when the payload hands back a numeric string instead of a number.
 *
 * Everything here therefore takes `unknown` and always returns a value the
 * template can render, rather than trusting the shape of third-party data.
 */

const DEFAULT_MAX_STARS = 5;

/** Whatever the payload gave us, coerced into a usable 0..maxStars rating. */
function normalizeRating(rating: unknown, maxStars: number): number {
  const value = typeof rating === 'number' ? rating : Number(rating);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(value, maxStars);
}

/**
 * Filled stars to draw. Rounds to the nearest whole star, preserving how the
 * dialog's main rating row already read (`Math.round(serpRating)`).
 */
export function filledStarCount(rating: unknown, maxStars: number = DEFAULT_MAX_STARS): number {
  return Math.round(normalizeRating(rating, maxStars));
}

/** Greyed-out stars making the row up to `maxStars`. Never negative. */
export function emptyStarCount(rating: unknown, maxStars: number = DEFAULT_MAX_STARS): number {
  return maxStars - filledStarCount(rating, maxStars);
}

/** The numeric rating shown beside the stars, e.g. "4.5". */
export function formatRating(rating: unknown, maxStars: number = DEFAULT_MAX_STARS): string {
  return normalizeRating(rating, maxStars).toFixed(1);
}
